import type { CheckoutData } from '@/types';

/**
 * Parses a price string and returns a number
 * Handles currency symbols, commas, and other formatting
 */
export const parsePrice = (priceString: string | undefined): number => {
  if (!priceString) return 0;
  const numericValue = priceString.replace(/[^\d.,]/g, '').replace(',', '.');
  return parseFloat(numericValue) || 0;
};

/**
 * Calculates the base amount (total - shipping) which includes any discounts, gift cards, and loyalty points
 * Used for Google Pay when shipping options change
 */
export const calculateBaseAmount = (
  amounts: CheckoutData['amounts'] | undefined,
): number => {
  if (!amounts) return 0;

  const total = parsePrice(amounts.total);
  const shipping = parsePrice(amounts.shipping);  

  // Base amount is total minus current shipping (preserves all applied credits)
  // The total already includes gift cards and loyalty points deductions
  // Ensure the result is never negative (can happen when gift cards exceed subtotal)
  return Math.max(0, total - shipping);
};

/**
 * Calculates discount amount for display
 * For checkout: uses the discount field
 * For buy now: calculates from price difference
 */
export const calculateDiscountAmount = (
  originalPrice: number,
  currentPrice: number,
  explicitDiscount?: string,
): number => {
  // If explicit discount is provided (checkout mode), use it
  if (explicitDiscount !== undefined) {
    return parsePrice(explicitDiscount);
  }

  // Otherwise calculate from price difference (buy now mode)
  const discount = originalPrice - currentPrice;
  return discount > 0 ? discount : 0;
};

/**
 * Builds display items for Google Pay
 * Ensures consistent display across initial load and updates
 */
export const buildDisplayItems = (params: {
  isBuyNow: boolean;
  currentAmount: number;
  checkoutData?: CheckoutData;
  productPrice?: string;
  productOriginalPrice?: string | null;
  shippingCost?: number;
  showShipping?: boolean;
}): google.payments.api.DisplayItem[] => {
  const {
    isBuyNow,
    currentAmount,
    checkoutData,
    productPrice,
    productOriginalPrice,
    shippingCost = 0,
    showShipping = false,
  } = params;

  const items: google.payments.api.DisplayItem[] = [];

  if (!isBuyNow && checkoutData?.amounts?.subtotal) {
    // Checkout mode with full amount details
    const checkoutAmounts = checkoutData.amounts;
    const subtotal = parsePrice(checkoutAmounts.subtotal);

    items.push({
      label: 'Subtotal',
      type: 'SUBTOTAL',
      price: subtotal.toFixed(2),
    });

    // Show regular discount first
    const discount = parsePrice(checkoutAmounts.discount);
    const roundedDiscount = parseFloat(discount.toFixed(2));
    if (roundedDiscount > 0) {
      items.push({
        label: 'Discount',
        type: 'LINE_ITEM',
        price: `-${roundedDiscount.toFixed(2)}`,
      });
    }

    // Show coupon discount second
    const couponDiscount = checkoutAmounts.couponDiscountDecimal || 0;
    const roundedCouponDiscount = parseFloat(couponDiscount.toFixed(2));
    if (roundedCouponDiscount > 0) {
      const couponCode = checkoutData.discountCode || 'COUPON';
      items.push({
        label: couponCode,
        type: 'LINE_ITEM',
        price: `-${roundedCouponDiscount.toFixed(2)}`,
      });
    }
    
    // Show gift cards if applied
    if (checkoutAmounts.giftCard && checkoutAmounts.giftCard.length > 0) {
      checkoutAmounts.giftCard.forEach((giftCard) => {
        if (giftCard.isApplied && giftCard.applied) {
          const appliedAmount = parsePrice(giftCard.applied);
          if (appliedAmount > 0) {
            items.push({
              label: `Gift Card (${giftCard.cardNumber})`,
              type: 'LINE_ITEM',
              price: `-${appliedAmount.toFixed(2)}`
            });
          }
        }
      });
    }
    
    // Show loyalty card if applied
    if (checkoutAmounts.loyaltyCard) {
      const loyaltyAmount = parsePrice(checkoutAmounts.loyaltyCard);
      if (loyaltyAmount > 0) {
        items.push({
          label: 'Loyalty Points',
          type: 'LINE_ITEM',
          price: `-${loyaltyAmount.toFixed(2)}`
        });
      }
    }
  } else if (isBuyNow && productPrice) {
    // Buy Now mode - use original price if available, otherwise use current price
    const originalPrice = productOriginalPrice
      ? parsePrice(productOriginalPrice)
      : parsePrice(productPrice);
    const discountedPrice = parsePrice(productPrice);

    items.push({
      label: 'Subtotal',
      type: 'SUBTOTAL',
      price: originalPrice.toFixed(2),
    });

    // Show discount if original price is different from current price
    const discountAmount = originalPrice - discountedPrice;
    if (discountAmount > 0) {
      items.push({
        label: 'Discount',
        type: 'LINE_ITEM',
        price: `-${discountAmount.toFixed(2)}`,
      });
    }
  } else {
    // Fallback - just show the current amount
    items.push({
      label: 'Subtotal',
      type: 'SUBTOTAL',
      price: currentAmount.toFixed(2),
    });
  }

  // Add shipping if requested
  if (showShipping) {
    items.push({
      label: 'Shipping',
      type: 'LINE_ITEM',
      price: shippingCost.toFixed(2),
    });
  }

  return items;
};
